import { useState } from 'react';
import {
  validateScorecard,
  hasScorecardErrors,
  type ScorecardErrors,
} from '@kula-ai/components';
import type { ScorecardType } from '@/types/interview';
import {
  buildScorecardFillData,
  type ScorecardFillValues,
} from './scorecardFill';
import {
  findInvalidAnswers,
  mergeScorecardErrors,
  normalizeScorecardAnswers,
} from './scorecardAnswerValidity';

interface FillFieldConfig {
  name: string | number;
  [key: string]: unknown;
}

export interface ScorecardValidationResult {
  /** Safe to submit. */
  valid: boolean;
  /** The (coerced) values to submit — yes_no lowercased, numeric stars to ints. */
  values: ScorecardFillValues;
}

/**
 * Gates a scorecard submit so the UI flags everything the backend would 422 on
 * (`err_invalid_answer`), before the API call. It combines:
 *  - kula-ai `validateScorecard` — required-but-empty mandatory fields, and
 *  - {@link findInvalidAnswers} — *present* answers that are invalid for their
 *    type (the BE checks these even when `mandatory: false`).
 * Auto-fixable answers (yes_no casing, numeric-string stars) are coerced first
 * so a correct-looking field isn't blocked.
 *
 * `validate(values)` returns `{ valid, values }`: when `valid` is false the
 * builder shows inline errors + scrolls and the caller must not submit; when
 * true, submit the returned (coerced) `values`. Pass `errors` +
 * `scrollToErrorToken` straight into `ScorecardFillBody`.
 */
export const useScorecardValidation = (
  scorecard: ScorecardType,
  formConfig: FillFieldConfig[]
) => {
  const [errors, setErrors] = useState<ScorecardErrors>({});
  const [scrollToErrorToken, setScrollToErrorToken] = useState(0);

  const validate = (
    rawValues: ScorecardFillValues
  ): ScorecardValidationResult => {
    const values = normalizeScorecardAnswers(rawValues, scorecard);
    const data = buildScorecardFillData(values, scorecard, formConfig);
    const result = mergeScorecardErrors(
      validateScorecard(data),
      findInvalidAnswers(data)
    );
    if (hasScorecardErrors(result)) {
      setErrors(result);
      setScrollToErrorToken((token) => token + 1);
      return { valid: false, values };
    }
    setErrors({});
    return { valid: true, values };
  };

  return { errors, scrollToErrorToken, validate };
};
